import React, { useState, useEffect } from "react"
import _isEmpty from "lodash/isEmpty"
import { NumericFormat } from "react-number-format"
import useWindowResize from "@/hooks/useWindowResize"

import QuantitySelector from "@/components/cart/QuantitySelector"
import InstallationServiceCart from "@/components/PreviewCart/v1/InstallationServiceCart"
import { getConfig } from "@/constants/config"
import {
  getPDPUrl,
  checkStock,
  sanitizeTextForAnalytics,
  pdpTypeFormat,
  getPresetUrl,
} from "@/utils/helper"
import { removeLineItems, removeCartItem } from "@/components/cart/cartHelper"
import { getChildItemsCount } from "@/components/cart/utils"
import { callOverlayEventInfoAnalytics } from "@/components/PreviewCart/v1/analytics"
import { addRemoveProductFromCartPreviewAnalytics } from "@/components/PreviewCart/v1/analytics"
import noImage from "@/public/images/image-missing.png"
import infoIcon from "@/public/icons/warning.svg"
import { INSTALL_SERVICES } from "@/utils/constants"
//import { cartPrerender } from "@/utils/prerender"

const PreviewCartTile = ({
  i18n,
  data,
  checkoutMode,
  currencySign,
  productEventInfo,
  presetConfigs = "",
  loading,
  qtyLoading,
  setLoading = () => {},
  ...props
}) => {
  const [width] = useWindowResize()
  const [quantity, setQuantity] = useState(data.quantity)
  const [previewCartAlert, setPreviewCartAlert] = useState({
    show: false,
    mssg: "",
  })
  const [pdpUrl, setpdpUrl] = useState("")
  const [scene7AccountName, setScene7AccountName] = useState("")
  const [swatchUrl, setSwatchUrl] = useState("")
  const [siteName, setSiteName] = useState("")
  const [isExceed, setIsExceed] = useState(false)
  const [installServiceData, setInstallServiceData] = useState({})
  const [hasInstallService, setHasInstallService] = useState(false)
  const { isBundleParent } = data

  let totalArea = ""
  let productType = ""
  let totalLabel = ""

  useEffect(() => {
    if (!(qtyLoading || loading)) {
      setQuantity(data.quantity)
    }
    if (data?.additionalData) setLabel()
    const isInstallService = isBundleParent
      ? data.bundleItems.filter(item => item.category === "Install Services")
          .length
      : data?.custom?.fields.hasOwnProperty("associatedService")
    setHasInstallService(isInstallService)
    const installServiceName =
      isInstallService && data?.custom?.fields?.associatedService
    const tempInstallServiceData = isBundleParent
      ? data.bundleItems.find(item => item.category === "Install Services")
      : props.cartData?.products?.find(item => item.sku === installServiceName)
    setInstallServiceData(tempInstallServiceData)
  }, [data])

  useEffect(() => {
    getConfig().then(config => {
      constructPdpUrl()
      const {
        general: { scene7AccountName = "", swatchUrl = "", siteName = "" },
      } = config
      setScene7AccountName(scene7AccountName)
      setSwatchUrl(swatchUrl)
      setSiteName(siteName)
    })
  }, [])

  useEffect(() => {
    if (!(qtyLoading || loading)) {
      setQuantity(data.quantity)
    }
  }, [qtyLoading, loading])

  const handleChange = qty => {
    setQuantity(qty)
    setPreviewCartAlert({ show: false, mssg: "" })
    props.onQuantityChange(
      data.sku,
      qty,
      data.isBundleParent,
      checkStock(data.backOrder, data.category),
      mssg => setPreviewCartAlert({ show: true, mssg }),
      data,
      data.masterVariantKey ?? ""
    )
  }

  const constructPdpUrl = async () => {
    let pdpUrl = await getPDPUrl(data.category, data.slug)
    if (
      data.category !== "Install Services" &&
      data.category !== "Design Services"
    ) {
      pdpUrl = `${pdpUrl}${pdpUrl?.includes("?type=part") ? "&" : "?"}skuId=${
        data.sku
      }`
    }
    setpdpUrl(pdpUrl)
  }

  const handleRemoveFromCart = data => {
    if (window.adobeDataLayer) {
      // here we need to wait for callback of API to add analytics
      // meanwhile we create productInfo and eventInfo objects and just add initialQuantity flag
      const { adobeDataLayer: { getState } = {} } = window
      window.pageObj = (getState && getState("page")) || {}
      window.pageObj.eventInfo = {
        eventAction: `cart preview:cart update:subtract`,
        eventName: "cart preview:cart update:subtract",
        eventType: "navigation",
        eventMsg: "n/a",
        eventStatus: "n/a",
        internalLinkName: "cart update",
        internalLinkPosition: "cart",
        internalLinkType: "cart:removal",
        internalLinkZoneName: "cart:preview",
        internalLinkURL: "n/a",
        clickInternalLinks: "true",
      }
      window.pageObj.productInfo = getProductInfo(data, true)
    }
    if (
      hasInstallService &&
      installServiceData &&
      data?.custom?.fields?.associatedServiceQty > 0
    ) {
      const items = [
        {
          ...installServiceData,
          quantity: data?.custom?.fields?.associatedServiceQty,
        },
        data,
      ]

      removeLineItems(items, data).finally(() => {
        setLoading(false)
        //cartPrerender()
      })
      return
    }
    removeCartItem(data, null, data.isBundleParent)
  }

  const getProductInfo = (data, needsRemovedQuantity = false) => {
    const { getState = () => {} } = window.adobeDataLayer
    const page = getState().page
    const siteWideCoupanCode = []
    const productCouponCode = []
    const discountPercents = []
    let productCoupondiscount = 0
    let siteWideDiscount = 0
    let newDiscountPercentage = 0

    if (data?.discountsList?.length) {
      data?.discountsList.forEach(discountItem => {
        if (discountItem.type === "sitewide") {
          // always apply sitewide discount first then remaining can be applied
          siteWideDiscount +=
            Number(data?.unitPrice) * (discountItem?.percent / 100)
          siteWideCoupanCode.push(discountItem?.code?.toLowerCase())
        } else {
          // there can be multiple product coupon codes
          discountPercents.push(discountItem?.percent)
          productCouponCode.push(discountItem?.code)
        }
      })
      if (discountPercents.length) {
        let priceAfterDiscount = Number(data?.unitPrice) - siteWideDiscount
        let temporaryCouponPrice
        for (const disc of discountPercents) {
          temporaryCouponPrice = Number(
            (priceAfterDiscount * (disc / 100)).toFixed(2)
          )
          priceAfterDiscount = priceAfterDiscount - temporaryCouponPrice
          productCoupondiscount += temporaryCouponPrice
        }
        productCoupondiscount = Number(productCoupondiscount.toFixed(2))
      }
    } else if (Number(data.totalBasePrice) > Number(data.unitPrice)) {
      newDiscountPercentage =
        ((Number(data.totalBasePrice) - Number(data.unitPrice)) /
          Number(data.totalBasePrice)) *
        100
      newDiscountPercentage = Number(newDiscountPercentage.toFixed(2))
    }
    return {
      productBrand: page?.site?.siteName,
      description: sanitizeTextForAnalytics(data.desc) || "n/a",
      frequency: "n/a",
      globalPromotionPrice: data?.discountPrice
        ? Number(data?.discountPrice)
        : Number(data?.unitPrice),
      globalDiscount: Number(data?.totalBasePrice) - Number(data?.unitPrice),
      itemType: data?.category || data?.businessUnitName || "",
      globalOffer: siteWideCoupanCode?.join("|") || "n/a",
      productCouponCode: productCouponCode?.join("|") || "n/a",
      isRecommended: false,
      isSubscription: false,
      priceState:
        data?.discountPercent > 0 || data?.discountAmount > 0
          ? "percentOff|" + data.discountPercent
          : newDiscountPercentage > 0
          ? "percentOff|" + newDiscountPercentage
          : "regular price",
      productBasePrice: Number(data?.totalBasePrice) || "n/a",
      productCategory: sanitizeTextForAnalytics(data?.category) || "n/a",
      productColor: sanitizeTextForAnalytics(data?.color) || "n/a",
      productCoupondiscount: productCoupondiscount || 0,
      productID: data.customerFacingSKU || data.sku || "n/a",
      productName: sanitizeTextForAnalytics(data.name) || "n/a",
      productPartnerBuyNow: false,
      productRoom: sanitizeTextForAnalytics(data.category) || "n/a",
      productSalePrice: data.discountPrice
        ? Number(data.discountPrice) || 0
        : Number(data.unitPrice) || 0,
      productSaleable: data.productSaleable ?? true,
      productStatus: data.availableQty >= 0 ? "in stock" : "out of stock",
      productSuperSku: data.superSku ? true : false,
      productTileName: sanitizeTextForAnalytics(data.name) || "n/a",
      quantity: needsRemovedQuantity ? 0 : data.quantity,
      initialQuantity: data.quantity,
      defaultImageName: data.image || "n/a",
      ratings: "n/a",
      numberOfReviews: "n/a",
      pdpType: pdpTypeFormat({
        businessUnitName: data.businessUnitName,
        ProductIsRetail: data.ProductIsRetail,
        discontinuedProductb: data.discontinuedProductb,
        ProductRequiredAccessoriesGroupsList:
          data.ProductRequiredAccessoriesGroupsList,
        superSku: data.superSku,
      }),
      productFindingMethod: "n/a",
      productCollectionsName:
        sanitizeTextForAnalytics(
          data.productCollectionsName || data.category
        ) || "n/a",
    }
  }

  const handleProductClick = () => {
    const productInfo = getProductInfo(data)
    callOverlayEventInfoAnalytics(pdpUrl, productEventInfo, productInfo, data)
  }

  const setLabel = () => {
    if (
      data.additionalData.roomInfo &&
      data.additionalData.roomInfo.length > 0
    ) {
      const roomInfoDetails = JSON.parse(data.additionalData.roomInfo[0])
      totalArea = roomInfoDetails.totalArea ? roomInfoDetails.totalArea : ""
      productType = roomInfoDetails.productType
        ? roomInfoDetails.productType
        : ""
      totalLabel = productType === "tile" ? i18n.squreFt : i18n.linearFt
    }
  }
  const presetConfigsData = presetConfigs ? JSON.parse(presetConfigs) : {}
  let imageUrl = data?.image
  if (
    !_isEmpty(presetConfigsData) &&
    siteName?.toLowerCase() === data?.brand?.toLowerCase()
  ) {
    const presetImageUrl = getPresetUrl(
      width,
      swatchUrl,
      data?.image,
      presetConfigsData,
      scene7AccountName
    )
    imageUrl = presetImageUrl
  }

  const handleExceed = () => {
    setIsExceed(true)
    setTimeout(() => setIsExceed(false), 6000)
  }

  const MRPPriceObj = data?.prices?.find(price => !price.customerGroup)

  const MRPPrice = data.isBundleParent
    ? data.bundleMRPPrice / 100
    : Number((MRPPriceObj?.value?.centAmount ?? 0) / 100).toFixed(2)

  const MRPDiscount = Number(
    ((Number(MRPPrice) - Number(data.unitPrice)) / Number(MRPPrice)) * 100
  ).toFixed()

  return (
    <>
      {props.comboChild && (
        <div className="preview-cart-item__item-of">
          {i18n.itemOf(props.comboChild.index, props.comboChild.count)}
        </div>
      )}
      <div className="preview-cart-item">
        <a href={pdpUrl} onClick={handleProductClick} data-testid="pdpClick">
          <img
            onError={e => (e.target.src = noImage?.src)}
            src={imageUrl || data.image}
            alt=""
            className="preview-cart-item__image"
          />
        </a>
        <div className="preview-cart-item__details">
          <a href={pdpUrl} onClick={handleProductClick}>
            <p className="preview-cart-item__name" data-testid="prodName">
              {checkoutMode
                ? data.name && data.category !== INSTALL_SERVICES
                  ? data.name + " " + data.desc
                  : `Installation Service For SKU ${
                      data.isBundleChildItem
                        ? data?.custom?.fields?.parentBundleSku
                        : data?.custom.fields.associatedProduct
                    }`
                : data.name}
            </p>
            <div className="preview-cart-item__header">
              {data.desc && (
                <div className="preview-cart-item__brand">{data.desc}</div>
              )}
            </div>
          </a>
          {data.category !== "Install Services" &&
            data.category !== "Design Services" &&
            data.color !== "Not Applicable" &&
            !data.isBundleParent && (
              <p className="preview-cart-item__finish" data-testid="prodColor">
                {i18n.colorFinish}: {data.color}
              </p>
            )}
          {data.isBundleParent && (
            <div className="cart-item__bundle-items preview-tile">
              {`${getChildItemsCount(data.bundleItems)} ${i18n.items}`}
            </div>
          )}
          {data.isBundleParent && (
            <div className="cart-item__bundle-badge preview-tile">
              {i18n.bundle}
            </div>
          )}
          {(productType === "tile" || productType === "trim") && (
            <div className="preview-cart-item__additional-Info">
              <span className="preview-cart-item__additional-Info-label">
                {i18n?.total?.toUpperCase()} {totalLabel?.toUpperCase()}
              </span>
              <span className="preview-cart-item__additional-Info-detail">
                : {totalArea}
              </span>
            </div>
          )}

          {previewCartAlert.show && (
            <div className="preview-cart-item__alert">
              <img src={infoIcon?.src} alt={previewCartAlert.mssg} />
              <span>{previewCartAlert.mssg}</span>
            </div>
          )}
          {!previewCartAlert.show &&
            data.outOfStock &&
            data.availableQty < 1 && (
              <div className="preview-cart-item__alert">
                <img src={infoIcon?.src} alt={i18n.outOfStock} />
                <span>{i18n.outOfStock}</span>
              </div>
            )}
          {!previewCartAlert.show &&
            data.outOfStock &&
            data.availableQty > 0 && (
              <div className="preview-cart-item__alert">
                <img
                  src={infoIcon?.src}
                  alt={i18n.qtyMoreThanStock(data.quantity, data.availableQty)}
                />
                <span>
                  {i18n.qtyMoreThanStock(data.quantity, data.availableQty)}
                </span>
              </div>
            )}

          <div className="preview-cart-item__footer">
            <div className="preview-cart-item__qty">
              {!checkoutMode ? (
                <QuantitySelector
                  source="cart-preview"
                  productData={data}
                  size="small"
                  value={quantity}
                  onChange={qty => handleChange(qty)}
                  loading={qtyLoading || loading}
                  setQuantity={setQuantity}
                />
              ) : (
                <span
                  className="preview-cart-item__qty-label"
                  data-testid="prodQuantity"
                >{`${i18n.qtyLabel}: ${data.quantity}`}</span>
              )}
            </div>
            {!props.comboChild && (
              <div className="preview-cart-item__price-details">
                <p className="preview-cart-item__price">
                  <NumericFormat
                    value={
                      data.discountPrice > 0
                        ? data.discountPrice * Number(data.quantity ?? 1)
                        : data.totalPrice
                    }
                    displayType={"text"}
                    thousandSeparator={true}
                    aria-description={i18n.unitPrice}
                    prefix={currencySign}
                    decimalScale={2}
                    fixedDecimalScale={true}
                    thousandsGroupStyle={"lakh"}
                  />
                </p>
                {data.discountPrice > 0 &&
                  data.discountPercent > 0 &&
                  !data.isBundleChildItem && (
                    <div className="preview-cart-item__discount-container">
                      <p className="preview-cart-item__offPercent">
                        {data.discountPercent
                          ? `${data.discountPercent}${i18n.discountPercentage}`
                          : `${currencySign}${data.discountAmount} ${i18n.off}`}
                      </p>
                      <p className="preview-cart-item__discount">
                        <NumericFormat
                          value={data.unitPrice * data.quantity}
                          displayType={"text"}
                          aria-description={i18n.strikedPrice}
                          thousandSeparator={true}
                          prefix={currencySign}
                          decimalScale={2}
                          fixedDecimalScale={true}
                          thousandsGroupStyle={"lakh"}
                        />
                      </p>
                    </div>
                  )}
                {MRPPrice &&
                  MRPPrice !== data.unitPrice &&
                  data.discountPercent > 0 &&
                  !data.isBundleChildItem && (
                    <div className="preview-cart-item__discount-container">
                      <p className="preview-cart-item__offPercent">
                        {MRPDiscount
                          ? `${MRPDiscount}${i18n.discountPercentage}`
                          : `${currencySign}${data.discountAmount} ${i18n.off}`}
                      </p>
                      <p>
                        <span>MRP: </span>
                        <span className="preview-cart-item__discount">
                          <NumericFormat
                            value={MRPPrice * data.quantity}
                            displayType={"text"}
                            aria-description={i18n.strikedPrice}
                            thousandSeparator={true}
                            prefix={currencySign}
                            decimalScale={2}
                            fixedDecimalScale={true}
                            thousandsGroupStyle={"lakh"}
                          />
                        </span>
                      </p>
                    </div>
                  )}
              </div>
            )}
          </div>
          {!checkoutMode && (
            <div
              className={`preview-cart-item__remove ${
                props.loading ? "--loading" : ""
              }`}
              onClick={() =>
                addRemoveProductFromCartPreviewAnalytics("cmp:click", data)
              }
            >
              <span
                role="button"
                tabIndex="0"
                aria-label={`${data.name} ${i18n.remove}`}
                onClick={() => handleRemoveFromCart(data)}
                data-testid={"removeFromCart"}
              >
                {i18n.remove}
              </span>
            </div>
          )}
        </div>
      </div>
      {isExceed && (
        <div className="preview-cart-item__alert">
          <img src={infoIcon.src} />
          <span>Quantity cannot be more than the product quantity</span>
        </div>
      )}
      {(hasInstallService &&
        installServiceData &&
        data?.custom?.fields?.associatedServiceQty > 0) ||
      (data.isBundleParent && hasInstallService) ? (
        <InstallationServiceCart
          key={installServiceData?.id}
          i18n={i18n}
          loading={props.loading}
          checkoutMode={checkoutMode || checkoutMode === "share"}
          data={installServiceData}
          productData={data}
          productQuantity={quantity}
          onQuantityChange={props.onQuantityChange}
          currencySign={currencySign}
          handleExceed={handleExceed}
          setLoading={setLoading}
        />
      ) : null}
    </>
  )
}

export default PreviewCartTile
